import { useCallback, useEffect, useMemo, useState } from "react";
import {
  transformFlatToHierarchicalSpend,
  HierarchicalSpendItem,
} from "../../utils";
import { CompanySpend } from "../../../../../../../legacy/queries/Companies";

export const useSpendBreakdown = (data: CompanySpend[] | undefined) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Transform flat data to hierarchical structure
  const hierarchicalData = useMemo(() => {
    if (!data) return [];

    const transformed = transformFlatToHierarchicalSpend(data);
    // Sort root items by value (descending) to prioritize "Total IT"
    return transformed.sort((a, b) => b.value - a.value);
  }, [data]);

  // Filter hierarchical data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return hierarchicalData;

    const filterRecursive = (
      items: HierarchicalSpendItem[]
    ): HierarchicalSpendItem[] => {
      return items.reduce((acc: HierarchicalSpendItem[], item) => {
        const matchesSearch = item.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        let filteredChildren: HierarchicalSpendItem[] = [];
        if (item.children) {
          filteredChildren = filterRecursive(item.children);
        }

        // Include item if it matches search OR has matching children
        if (matchesSearch || filteredChildren.length > 0) {
          acc.push({
            ...item,
            children:
              filteredChildren.length > 0 ? filteredChildren : item.children,
          });
        }

        return acc;
      }, []);
    };

    return filterRecursive(hierarchicalData);
  }, [hierarchicalData, searchTerm]);

  // Get total value for progress calculations (use the largest root item which should be "Total IT")
  const totalValue = useMemo(() => {
    if (filteredData.length === 0) return 0;

    // Use the largest value as the total for percentage calculations
    return Math.max(...filteredData.map((item) => item.value));
  }, [filteredData]);

  // Auto-expand specific levels on initial load and when searching
  useEffect(() => {
    if (filteredData.length > 0) {
      const newExpanded = new Set<string>();

      // Auto-expand root level (Total IT)
      filteredData.forEach((item) => {
        newExpanded.add(item.id);

        // Auto-expand only "Total External IT" but not its children
        if (item.children) {
          item.children.forEach((child: HierarchicalSpendItem) => {
            if (child.name === "Total External IT") {
              newExpanded.add(child.id);
              // Do not expand Total External IT's children by default
            }
            // Do not expand "Total Internal IT" by default
          });
        }
      });

      // When searching, expand all matching items and their parents for better visibility
      if (searchTerm.trim()) {
        const expandMatching = (
          items: HierarchicalSpendItem[],
          parentIds: string[] = []
        ) => {
          items.forEach((item) => {
            const isMatch = item.name
              .toLowerCase()
              .includes(searchTerm.toLowerCase());

            if (isMatch) {
              // Expand the matching item
              newExpanded.add(item.id);
              // Expand all parent items
              parentIds.forEach((parentId) => newExpanded.add(parentId));
            }

            if (item.children) {
              expandMatching(item.children, [...parentIds, item.id]);
            }
          });
        };
        expandMatching(filteredData);
      }

      setExpandedItems(newExpanded);
    }
  }, [filteredData, searchTerm]);

  const handleToggleExpand = useCallback(
    (itemId: string) => {
      const newExpanded = new Set(expandedItems);

      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }

      setExpandedItems(newExpanded);
    },
    [expandedItems]
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  return {
    searchTerm,
    handleSearchChange,
    filteredData,
    totalValue,
    expandedItems,
    handleToggleExpand,
  };
};
